import { useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Phone, Check, CheckCheck, X, PartyPopper } from "lucide-react";
import { useBookings, usePatchBooking, type Booking } from "@/hooks/useBookings.js";
import { useAuth } from "@/auth/useAuth.js";

function getTitleIt(title: unknown): string {
  if (title && typeof title === "object" && "it" in title) return String((title as { it: unknown }).it ?? "");
  return "";
}
const SLOT_ORDER: Record<string, number> = { mattina: 0, pomeriggio: 1, sera: 2 };
const localDay = (d: Date) => new Intl.DateTimeFormat("en-CA").format(d);
const fmtDay = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" });

type Section = { key: string; label: string; tone: "risk" | "accent" | "neutral"; items: Booking[] };

function buildSections(items: Booking[], today: string): Section[] {
  const active = items.filter((b) => b.status === "richiesta" || b.status === "confermata");
  const bySlot = (a: Booking, b: Booking) =>
    (SLOT_ORDER[a.preferred_slot ?? ""] ?? 9) - (SLOT_ORDER[b.preferred_slot ?? ""] ?? 9);

  const overdue = active
    .filter((b) => b.preferred_date && b.preferred_date < today)
    .sort((a, b) => (a.preferred_date! < b.preferred_date! ? -1 : 1) || bySlot(a, b));
  const undated = active.filter((b) => !b.preferred_date);
  const upcoming = active
    .filter((b) => b.preferred_date && b.preferred_date >= today)
    .sort((a, b) => (a.preferred_date! < b.preferred_date! ? -1 : a.preferred_date! > b.preferred_date! ? 1 : bySlot(a, b)));

  const sections: Section[] = [];
  if (overdue.length > 0) sections.push({ key: "overdue", label: "Arretrate (data passata, ancora aperte)", tone: "risk", items: overdue });

  const byDay = new Map<string, Booking[]>();
  for (const b of upcoming) {
    const list = byDay.get(b.preferred_date!) ?? [];
    list.push(b);
    byDay.set(b.preferred_date!, list);
  }
  const tomorrow = localDay(new Date(Date.now() + 86_400_000));
  for (const [day, list] of byDay) {
    const label = day === today ? `Oggi · ${fmtDay(day)}` : day === tomorrow ? `Domani · ${fmtDay(day)}` : fmtDay(day);
    sections.push({ key: day, label, tone: day === today ? "accent" : "neutral", items: list });
  }
  if (undated.length > 0) sections.push({ key: "undated", label: "Senza data (da programmare)", tone: "neutral", items: undated });
  return sections;
}

export default function Agenda(): JSX.Element {
  const { role } = useAuth();
  const canEdit = role === "owner" || role === "admin" || role === "editor";
  const { data, isLoading, error } = useBookings();
  const patch = usePatchBooking();
  const today = localDay(new Date());

  const sections = useMemo(() => buildSections(data ?? [], today), [data, today]);
  const open = sections.reduce((s, sec) => s + sec.items.length, 0);

  const setStatus = (b: Booking, status: "confermata" | "completata" | "annullata", label: string) => {
    patch.mutate({ id: b.id, status }, {
      onSuccess: () => toast.success(label),
      onError: (e) => toast.error(e instanceof Error ? e.message : "Operazione non riuscita"),
    });
  };

  return (
    <div className="space-y-[22px]">
      <header>
        <h1 className="font-display text-[27px] font-bold tracking-tight text-ink">Agenda visite</h1>
        <p className="mt-[5px] text-[13.5px] text-ink-soft">
          {isLoading ? "Carico…" : `${open} ${open === 1 ? "visita aperta" : "visite aperte"} (richieste e confermate)`}
        </p>
      </header>

      {error ? (
        <p className="rounded-[5px] border border-risk/30 bg-risk/10 p-3 text-[13px] text-risk">Errore: {(error as Error).message}</p>
      ) : null}

      {!isLoading && sections.length === 0 ? (
        <div className="ticks relative flex items-center gap-3 rounded-[5px] border border-rule bg-surface p-[24px]">
          <PartyPopper className="size-5 text-success" />
          <p className="text-[13px] text-ink-soft">Nessuna visita in agenda. Le richieste arrivano dal sito e dall'Inbox lead.</p>
        </div>
      ) : null}

      {sections.map((sec) => (
        <div key={sec.key} className="ticks relative rounded-[5px] border border-rule bg-surface">
          <div className="flex items-center gap-[10px] border-b border-rule p-[13px_16px]">
            <CalendarDays className={`size-4 ${sec.tone === "risk" ? "text-risk" : sec.tone === "accent" ? "text-accent" : "text-ink-mute"}`} />
            <h3 className={`text-[13px] font-semibold ${sec.tone === "risk" ? "text-risk" : ""}`}>{sec.label}</h3>
            <span className="micro ml-auto">{sec.items.length}</span>
          </div>
          <div>
            {sec.items.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center gap-[12px] border-b border-rule-soft p-[11px_16px] last:border-b-0 hover:bg-surface-2">
                <span className="inline-flex h-[22px] w-[86px] items-center justify-center rounded-full border border-rule bg-surface-2 font-mono text-[10.5px] uppercase text-ink-soft">
                  {b.preferred_slot ?? "orario?"}
                </span>
                <span className="min-w-0 text-[13px]">
                  <b className="font-semibold text-ink">{b.name}</b>
                  {b.listing ? (
                    <span className="text-ink-soft"> · <Link to={`/listings`} className="hover:text-accent">{getTitleIt(b.listing.title) || b.listing.slug}</Link>{b.listing.city ? ` (${b.listing.city})` : ""}</span>
                  ) : null}
                </span>
                {b.phone ? (
                  <a href={`tel:${b.phone}`} className="inline-flex items-center gap-[5px] font-mono text-[11.5px] text-ink-mute hover:text-accent">
                    <Phone className="size-3" />{b.phone}
                  </a>
                ) : null}
                <span className={`inline-flex h-[22px] items-center rounded-full border px-[9px] font-mono text-[10.5px] uppercase ${b.status === "confermata" ? "border-success/50 bg-success/10 text-success" : "border-accent/50 bg-accent/10 text-accent"}`}>
                  {b.status}
                </span>
                {canEdit ? (
                  <span className="ml-auto flex gap-[6px]">
                    {b.status === "richiesta" ? (
                      <ActionBtn onClick={() => setStatus(b, "confermata", "Visita confermata")} disabled={patch.isPending} tone="success">
                        <Check className="size-[13px]" /> Conferma
                      </ActionBtn>
                    ) : (
                      <ActionBtn onClick={() => setStatus(b, "completata", "Visita completata")} disabled={patch.isPending} tone="success">
                        <CheckCheck className="size-[13px]" /> Completata
                      </ActionBtn>
                    )}
                    <ActionBtn onClick={() => setStatus(b, "annullata", "Visita annullata")} disabled={patch.isPending} tone="risk">
                      <X className="size-[13px]" /> Annulla
                    </ActionBtn>
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ActionBtn({ children, onClick, disabled, tone }: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone: "success" | "risk";
}): JSX.Element {
  const hover = tone === "success" ? "hover:border-success/60 hover:text-success" : "hover:border-risk/60 hover:text-risk";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-[26px] items-center gap-[6px] whitespace-nowrap rounded-[5px] border border-rule bg-surface-2 px-[9px] text-[12px] text-ink-soft transition-colors disabled:opacity-60 ${hover}`}
    >
      {children}
    </button>
  );
}
